/**
 * The homepage hero: the name across the full viewport, the positioning line
 * with its call to action, and the "trusted by" logo strip.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useLocales } from "../../core/i18n";
import { getFeaturedOrganizations, useSiteSettings } from "../../core/site";
import { CONTAINER } from "../../ui/shell";
import LogoShowcase from "./LogoShowcase";
import { useContainerOffset } from "./useContainerOffset";

/**
 * Content-sized hero, no viewport-height spacers. Reading order: name, what
 * she does (display size), CTA, who trusts her. The CTA only shows when the
 * site is open to work.
 */
const HomeHero = () => {
	const { t } = useLocales();
	const { openToWork } = useSiteSettings();
	const heroOffset = useContainerOffset();

	return (
		<header className="bg-pp-bg pt-12 sm:pt-16 lg:pt-10 pb-16 sm:pb-24 overflow-x-hidden">
			{/* CHLOÉ aligns with the content grid, LAVRAT touches the right screen edge. */}
			<h1
				className="font-sligoil font-bold text-pp-ink tracking-tight leading-none w-full"
				style={{ fontSize: "clamp(2.75rem, 16vw, 14rem)" }}
			>
				<span
					className="text-pp-accent flex justify-start animate-name-from-left"
					style={{ paddingLeft: heroOffset }}
				>
					CHLOÉ
				</span>
				{/* A real space between the two words: the spans are flex blocks, so
				    without it the accessible name reads "CHLOÉLAVRAT". It has no
				    visual effect between two block-level lines. */}{" "}
				<span
					className="flex justify-end animate-name-from-right"
					style={{ animationDelay: "120ms", paddingRight: heroOffset }}
				>
					LAVRAT
				</span>
			</h1>

			<div className={`${CONTAINER} w-full`}>
				<div
					className="mt-10 sm:mt-14 lg:mt-12 animate-line-reveal"
					style={{ animationDelay: "240ms" }}
				>
					<div className="max-w-5xl">
						<p className="font-sligoil font-bold text-pp-ink tracking-tight text-pp-display-sm">
							{t("hero.positioning")}
						</p>
						<p className="font-mono text-xs sm:text-sm text-pp-ink uppercase tracking-pp-label mt-5 sm:mt-6">
							{openToWork ? t("hero.roleLine") : t("showcase.roleLine")}
							<span className="pp-cursor" aria-hidden="true">
								▌
							</span>
						</p>
					</div>
					{/* The buyer's path is hero → services → contact; CONTACT stays in the nav.
					    The button sits with the sentence it answers, not across the page. */}
					{openToWork && (
						<Link to="/services" className="pp-btn inline-flex mt-6 whitespace-nowrap">
							{t("hero.ctaWorkWithMe")}
						</Link>
					)}
				</div>

				{/* The marquee bleeds to the viewport edges. */}
				<div
					className="mt-14 sm:mt-20 pt-6 border-t border-pp-rule animate-line-reveal"
					style={{ animationDelay: "320ms" }}
				>
					<p className="font-mono text-pp-label text-pp-muted uppercase tracking-pp-label mb-6">
						{t("homepage.logosEyebrow")}
					</p>
					<LogoShowcase logos={getFeaturedOrganizations()} bleed={heroOffset} />
				</div>
			</div>
		</header>
	);
};

export default HomeHero;
